<template>
  <v-row class="fill-height">
    <v-dialog
      v-model="errorDialog"
      width="500">
      <v-card>
        <v-card-title class="text-h4 red white--text">
          カレンダーCSVダウンロードエラー
        </v-card-title>
        <v-card-text class="my-4">
          {{ errorMessage }}
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            text
            @click="errorDialog = false">
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-col>
      <v-sheet height="64">
        <v-toolbar
          flat
          color="white">
          <v-toolbar-title>{{ title }}</v-toolbar-title>
          <v-btn
            fab
            text
            small
            color="grey darken-2"
            @click="prev">
            <v-icon small>
              mdi-chevron-left
            </v-icon>
          </v-btn>
          <v-btn
            fab
            text
            small
            color="grey darken-2"
            class="mr-4"
            @click="next">
            <v-icon small>
              mdi-chevron-right
            </v-icon>
          </v-btn>
          <v-spacer />
          <v-menu
            bottom
            right>
            <template v-slot:activator="{ on }">
              <v-btn
                outlined
                color="grey darken-2"
                class="mx-4 my-2"
                v-on="on">
                <span>{{ eventTypeToLabel[eventType] }}</span>
                <v-icon right>
                  mdi-menu-down
                </v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item
                v-for="(item, key) in eventTypeToLabel"
                :key="key"
                @click="changeEventType(key)">
                <v-list-item-title>
                  {{ item }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-menu
            v-if="!$isMobile()"
            ref="csvStartMenu"
            v-model="csvStartMenu"
            bottom
            :close-on-content-click="false"
            :return-value.sync="csvStartDate"
            transition="scale-transition"
            offset-y
            max-width="400px">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                outlined
                color="grey darken-2"
                v-bind="attrs"
                v-on="on">
                <v-icon left>
                  mdi-calendar
                </v-icon>
                開始年月：
                <span>{{ csvStartDate }}</span>
                <v-icon right>
                  mdi-menu-down
                </v-icon>
              </v-btn>
            </template>
            <v-date-picker
              v-model="csvStartDate"
              type="month"
              no-title
              scrollable
              locale="ja-jp">
              <v-spacer />
              <v-btn
                text
                color="primary"
                @click="csvStartMenu = false">
                キャンセル
              </v-btn>
              <v-btn
                text
                color="primary"
                @click="$refs.csvStartMenu.save(csvStartDate)">
                OK
              </v-btn>
            </v-date-picker>
          </v-menu>
          <v-menu
            v-if="!$isMobile()"
            ref="csvEndMenu"
            v-model="csvEndMenu"
            bottom
            :close-on-content-click="false"
            :return-value.sync="csvEndDate"
            transition="scale-transition"
            offset-y
            max-width="400px">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                outlined
                color="grey darken-2"
                v-bind="attrs"
                v-on="on">
                <v-icon left>
                  mdi-calendar
                </v-icon>
                終了年月：
                <span>{{ csvEndDate }}</span>
                <v-icon right>
                  mdi-menu-down
                </v-icon>
              </v-btn>
            </template>
            <v-date-picker
              v-model="csvEndDate"
              type="month"
              no-title
              scrollable
              locale="ja-jp">
              <v-spacer />
              <v-btn
                text
                color="primary"
                @click="csvEndMenu = false">
                キャンセル
              </v-btn>
              <v-btn
                text
                color="primary"
                @click="$refs.csvEndMenu.save(csvEndDate)">
                OK
              </v-btn>
            </v-date-picker>
          </v-menu>
          <VueJsonToCsv
            v-if="!$isMobile()"
            :json-data-generator="downloadCSV"
            :csv-title="csvTitle"
            :labels="labels">
            <v-btn
              class="mx-2 my-2"
              color="info">
              <v-icon small>
                mdi-download
              </v-icon>
              カレンダーCSVをDL
            </v-btn>
          </VueJsonToCsv>
        </v-toolbar>
      </v-sheet>
      <v-sheet height="100%">
        <v-calendar
          ref="calendar"
          v-model="focus"
          locale="ja-jp"
          color="primary"
          :day-format="dayFormatter"
          :events="filteredEvents"
          :event-color="getEventColor"
          :now="today"
          :type="type"
          @click:event="showEvent"
          @click:more="viewDay"
          @click:date="viewDay"
          @change="updateRange" />
      </v-sheet>
    </v-col>
  </v-row>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import VueJsonToCsv from 'vue-json-to-csv-async/src/JsonToCsv'
import { phaseMixin } from '../mixins/phaseMixin.js'
import moment from 'moment'

const shigoShis = [
  '子丑',
  '丑子',
  '寅亥',
  '亥寅',
  '卯戌',
  '戌卯',
  '辰酉',
  '酉辰',
  '申巳',
  '巳申',
  '未午',
  '午未',
]

const hankaiShis = [
  '子辰',
  '辰子',
  '辰申',
  '申辰',
  '子申',
  '申子',
  '丑巳',
  '巳丑',
  '巳酉',
  '酉巳',
  '丑酉',
  '酉丑',
  '寅午',
  '午寅',
  '午戌',
  '戌午',
  '寅戌',
  '戌寅',
  '卯未',
  '未卯',
  '未亥',
  '亥未',
  '卯亥',
  '亥卯',
]

const gaiShis = [
  '子未',
  '未子',
  '丑午',
  '午丑',
  '寅巳',
  '巳寅',
  '卯辰',
  '辰卯',
  '亥申',
  '申亥',
  '酉戌',
  '戌酉',
]

const seikikeiShis = ['寅巳', '巳寅', '申巳', '巳申', '寅申', '申寅']
const kokikeiShis = ['丑戌', '戌丑', '戌未', '未戌', '丑未', '未丑']
const taichuShis = [
  '子午',
  '午子',
  '丑未',
  '未丑',
  '寅申',
  '申寅',
  '卯酉',
  '酉卯',
  '辰戌',
  '戌辰',
  '巳亥',
  '亥巳',
]
const tenkokuKans = [
  '甲庚',
  '庚甲',
  '甲戊',
  '戊甲',
  '乙辛',
  '辛乙',
  '乙己',
  '己乙',
  '丙壬',
  '壬丙',
  '丙庚',
  '庚丙',
  '丁辛',
  '辛丁',
  '丁癸',
  '癸丁',
  '戊壬',
  '壬戊',
  '戊甲',
  '甲戊',
  '己乙',
  '乙己',
  '己癸',
  '癸己',
]
const haShis = ['子酉', '酉子', '丑辰', '辰丑', '卯午', '午卯', '未戌', '戌未']

const sekkiList = [
  '立春',
  '雨水',
  '啓蟄',
  '春分',
  '清明',
  '穀雨',
  '立夏',
  '小満',
  '芒種',
  '夏至',
  '小暑',
  '大暑',
  '立秋',
  '処暑',
  '白露',
  '秋分',
  '寒露',
  '霜降',
  '立冬',
  '小雪',
  '大雪',
  '冬至',
  '小寒',
  '大寒',
]

export default {
  components: {
    VueJsonToCsv,
  },
  mixins: [phaseMixin],
  data: () => ({
    focus: '',
    today: null,
    type: 'month',
    eventType: 'oneself',
    eventTypeToLabel: {
      oneself: '自己/結果',
      work: '仕事/課程',
      society: '社会/始め',
      safeDay: '安牌日',
    },
    start: null,
    end: null,
    selectedEvent: {},
    selectedElement: null,
    selectedOpen: false,
    events: [],
    oldCalendar: null,
    sanmeiPhase: {
      shigo: {
        name: '支合',
        color: 'orange',
      },
      hankai: {
        name: '半会',
        color: 'yellow darken-2',
      },
      daihankai: {
        name: '大半会',
        color: 'yellow darken-1',
      },
      ricchin: {
        name: '律音',
        color: 'green',
      },
      gai: {
        name: '害',
        color: 'grey',
      },
      jikei: {
        name: '自刑',
        color: 'grey',
      },
      oukikei: {
        name: '旺気刑',
        color: 'grey',
      },
      seikikei: {
        name: '生貴刑',
        color: 'grey',
      },
      kokikei: {
        name: '庫気刑',
        color: 'grey',
      },
      taichu: {
        name: '対沖',
        color: 'grey',
      },
      nacchin: {
        name: '納音',
        color: 'pink lighten-4',
      },
      tenkoku: {
        name: '天剋地冲',
        color: 'brown',
      },
      ha: {
        name: '破',
        color: 'grey',
      },
      tenshusatsu: {
        name: '天中殺',
        color: 'red',
      },
    },
    senjitsu: {
      sanrinbo: {
        name: '三隣亡',
        color: 'blue lighten-3',
      },
      ichiryu: {
        name: '一粒万倍日',
        color: 'lime darken-2',
      },
      fujoujunichi: {
        name: '不成就日',
        color: 'indigo darken-4',
      },
    },
    gedan: {
      tenshabi: {
        name: '天赦日',
        color: 'light-green darken-1',
      },
    },
    csvData: [],
    csvTitle: '自分カレンダー',
    labels: {
      subject: { title: 'Subject' },
      startDate: { title: 'Start Date' },
      startTime: { title: 'Start Time' },
      description: { title: 'Description' },
    },
    csvStartDate: new Date().toISOString().substr(0, 7),
    csvStartMenu: false,
    csvStartModal: false,
    csvEndDate: moment().add(1, 'year').format('YYYY-MM'),
    csvEndMenu: false,
    csvEndModal: false,
    errorDialog: false,
    errorMessage: ''
  }),
  computed: {
    title() {
      const { start, end } = this
      if (!start || !end) {
        return ''
      }
      const startMonth = this.monthFormatter(start)
      const startYear = start.year + '年'
      return `${startYear} ${startMonth}`
    },
    monthFormatter() {
      return this.$refs.calendar.getFormatter({
        timeZone: 'UTC',
        month: 'long',
      })
    },
    dayFormatter() {
      return (timestamp) => {
        let rokuyou = ''
        if (this.oldCalendar != null) {
          rokuyou = !this.oldCalendar.datelist[timestamp.date]
            ? ''
            : this.oldCalendar.datelist[timestamp.date].rokuyou
        }
        return `${timestamp.day} ${rokuyou}`
      }
    },
    filteredEvents() {
      if (this.eventType === 'safeDay') {
        return this.events.filter(
          (x) =>
            x.type === this.eventType ||
                        x.name === '一粒万倍日' ||
                        x.name === '天赦日'
        )
      } else {
        return this.events.filter(
          (x) => x.type === this.eventType || x.type === 'senjitsu'
        )
      }
    },
    ...mapState(['eto60', 'user_meishiki']),
    ...mapGetters(['etoDayIndex']),
  },
  mounted() {
    this.$refs.calendar.checkChange()
  },
  methods: {
    viewDay({ date }) {
      this.focus = date

      // 画面遷移パラメータ準備
      const sanmeiPhases = this.events.filter((x) => x.start === date)
      const rokuyou = this.oldCalendar.datelist[date].rokuyou

      // 日にちをクリックしたら画面遷移
      this.$router.push({
        name: 'calendar-day-date',
        params: { date, sanmeiPhases, rokuyou },
      })
    },
    getEventColor(event) {
      return event.color
    },
    setToday() {
      this.focus = this.today
    },
    prev() {
      this.$refs.calendar.prev()
    },
    next() {
      this.$refs.calendar.next()
    },
    showEvent({ event }) {
      this.viewDay({ date: event.start })
    },
    async updateRange({ start, end }) {
      this.start = start
      this.end = end

      // 旧暦取得WebAPI呼び出し
      await this.getOldCalendar()
      // 位相法適用
      this.applySanmeiPhase()
    },
    async getOldCalendar() {
      try {
        const res = await this.$axios.get(
          'https://koyomi.zingsystem.com/api/',
          {
            params: {
              mode: 'm',
              cnt: 1,
              targetyyyy: this.start.year,
              targetmm: this.start.month,
              targetdd: '01' // 利用APIの仕様上、日付は1日固定
            },
          }
        )
        this.oldCalendar = res.data

        // 節気の設定
        // 節気の境目(新暦の月で節気が変わるはじめの日)を取得
        let sekkiDate = ''
        for (let [key, value] of Object.entries(
          this.oldCalendar.datelist
        )) {
          if (value.sekki !== '') {
            sekkiDate = key
            break
          }
        }
        let sekkiIndex =
                    (sekkiList.indexOf(
                      this.oldCalendar.datelist[sekkiDate].sekki
                    ) -
                        1 +
                        24) %
                    24
        let sekkiStr = sekkiList[sekkiIndex]

        // 節目節気より前の節気設定
        for (let [, value] of Object.entries(
          this.oldCalendar.datelist
        )) {
          if (value.sekki === '') {
            value.sekki = sekkiStr
          }
          // 節気が切り替わり時
          else if (sekkiStr !== value.sekki) {
            sekkiIndex = (sekkiIndex + 1) % 24
            value.sekki = sekkiList[sekkiIndex]
            sekkiStr = value.sekki
          }
        }
      } catch (error) {
        //console.error('旧暦取得WebAPI失敗', error)
      }
    },
    applySanmeiPhase() {
      const { start, end } = this
      if (!start || !end) {
        return
      }
      // イベントの設定
      this.events = this.createSanmeiPhase('oneself') // 初期化
      this.events.push(...this.createSanmeiPhase('work'))
      this.events.push(...this.createSanmeiPhase('society'))
      this.events.push(...this.createSafeDay())
    },
    createSanmeiPhase(eventType) {
      const events = []

      // 日ごとの位相情報計算のベース日付（月の始めと終わり）
      const targetDateYYYYMM = this.start.date.slice(0, 8) // YYYY-MM-形式
      // 対象月の始めの日の干支位置計算
      let targetEtoDayIndex = this.etoDayIndex(this.start.date)

      // 日ごとの位相情報を計算
      // 対象期間(月)分ループ
      for (let i = this.start.day; i <= this.end.day; i++) {
        const targetDateDD = ('00' + i).slice(-2)
        const targetDateYYYYMMDD = `${targetDateYYYYMM}${targetDateDD}`

        // 必要情報取得
        const targetNittyu = this.eto60[targetEtoDayIndex]
        const targetNittyuKan = targetNittyu.slice(0, 1)
        const targetNittyuShi = targetNittyu.slice(-1)

        let userEtoIndex, userKan, userShi
        switch (eventType) {
        case 'oneself':
          userEtoIndex = this.user_meishiki.santyu.etoDayIndex
          userKan = this.user_meishiki.santyu.day1
          userShi = this.user_meishiki.santyu.day2
          break
        case 'work':
          userEtoIndex = this.user_meishiki.santyu.etoMonthIndex
          userKan = this.user_meishiki.santyu.month1
          userShi = this.user_meishiki.santyu.month2
          break
        case 'society':
          userEtoIndex = this.user_meishiki.santyu.etoYearIndex
          userKan = this.user_meishiki.santyu.year1
          userShi = this.user_meishiki.santyu.year2
          break
        default:
          break
        }

        const eventTemplate = {
          name: '',
          start: targetDateYYYYMMDD,
          end: targetDateYYYYMMDD,
          color: '',
          type: eventType,
          nittyu: targetNittyu,
        }

        // 選日 + 14.天中殺
        if (eventType === 'oneself') {
          // 選日イベントの設定
          const senjitsuEvents = this.createSenjitsu(
            targetNittyuKan,
            targetNittyuShi,
            targetDateYYYYMMDD
          )
          events.push(...senjitsuEvents)

          // 14.天中殺
          let hasTenshusatsu = false
          // 戌亥天中殺
          if (userEtoIndex >= 1 && userEtoIndex <= 10) {
            if (
              targetNittyuShi === '戌' ||
                            targetNittyuShi === '亥'
            ) {
              hasTenshusatsu = true
            }
            // 申酉天中殺
          } else if (userEtoIndex >= 11 && userEtoIndex <= 20) {
            if (
              targetNittyuShi === '申' ||
                            targetNittyuShi === '酉'
            ) {
              hasTenshusatsu = true
            }
            // 午未天中殺
          } else if (userEtoIndex >= 21 && userEtoIndex <= 30) {
            if (
              targetNittyuShi === '午' ||
                            targetNittyuShi === '未'
            ) {
              hasTenshusatsu = true
            }
            // 辰巳天中殺
          } else if (userEtoIndex >= 31 && userEtoIndex <= 40) {
            if (
              targetNittyuShi === '辰' ||
                            targetNittyuShi === '巳'
            ) {
              hasTenshusatsu = true
            }
            // 寅卯天中殺
          } else if (userEtoIndex >= 41 && userEtoIndex <= 50) {
            if (
              targetNittyuShi === '寅' ||
                            targetNittyuShi === '卯'
            ) {
              hasTenshusatsu = true
            }
            // 子丑天中殺
          } else if (userEtoIndex >= 51 && userEtoIndex <= 60) {
            if (
              targetNittyuShi === '子' ||
                            targetNittyuShi === '丑'
            ) {
              hasTenshusatsu = true
            }
          }
          // event追加
          if (hasTenshusatsu) {
            const event = Object.create(eventTemplate)
            event.name = this.sanmeiPhase.tenshusatsu.name
            event.color = this.sanmeiPhase.tenshusatsu.color
            events.push(event)
          }
        }

        // 1.支合
        if (
          shigoShis.includes(targetNittyuShi + userShi) ||
                    shigoShis.includes(userShi + targetNittyuShi)
        ) {
          const event = Object.create(eventTemplate)
          event.name = this.sanmeiPhase.shigo.name
          event.color = this.sanmeiPhase.shigo.color
          events.push(event)
        }

        // 2.半会, 3.大半会
        if (
          hankaiShis.includes(targetNittyuShi + userShi) ||
                    hankaiShis.includes(userShi + targetNittyuShi)
        ) {
          // 大半合判定
          // event追加
          if (targetNittyuKan === userKan) {
            const event = Object.create(eventTemplate)
            event.name = this.sanmeiPhase.daihankai.name
            event.color = this.sanmeiPhase.daihankai.color
            events.push(event)
          } else {
            const event = Object.create(eventTemplate)
            event.name = this.sanmeiPhase.hankai.name
            event.color = this.sanmeiPhase.hankai.color
            events.push(event)
          }
        }

        // 4.律音
        if (targetNittyu === userKan + userShi) {
          const event = Object.create(eventTemplate)
          event.name = this.sanmeiPhase.ricchin.name
          event.color = this.sanmeiPhase.ricchin.color
          events.push(event)
        }

        // 5.害
        if (
          gaiShis.includes(targetNittyuShi + userShi) ||
                    gaiShis.includes(userShi + targetNittyuShi)
        ) {
          const event = Object.create(eventTemplate)
          event.name = this.sanmeiPhase.gai.name
          event.color = this.sanmeiPhase.gai.color
          events.push(event)
        }

        // 6.自刑
        const jikeiShis = ['辰', '午', '酉', '亥']
        if (
          targetNittyuShi === userShi &&
                    jikeiShis.includes(targetNittyuShi)
        ) {
          const event = Object.create(eventTemplate)
          event.name = this.sanmeiPhase.jikei.name
          event.color = this.sanmeiPhase.jikei.color
          events.push(event)
        }

        // 7.旺気刑
        if (
          targetNittyuShi === '子' && userShi === '卯' ||
                    targetNittyuShi === '卯' && userShi === '子'
        ) {
          const event = Object.create(eventTemplate)
          event.name = this.sanmeiPhase.oukikei.name
          event.color = this.sanmeiPhase.oukikei.color
          events.push(event)
        }

        // 8.生貴刑
        if (
          seikikeiShis.includes(targetNittyuShi + userShi) ||
                    seikikeiShis.includes(userShi + targetNittyuShi)
        ) {
          const event = Object.create(eventTemplate)
          event.name = this.sanmeiPhase.seikikei.name
          event.color = this.sanmeiPhase.seikikei.color
          events.push(event)
        }

        // 9.庫気刑
        if (
          kokikeiShis.includes(targetNittyuShi + userShi) ||
                    kokikeiShis.includes(userShi + targetNittyuShi)
        ) {
          const event = Object.create(eventTemplate)
          event.name = this.sanmeiPhase.kokikei.name
          event.color = this.sanmeiPhase.kokikei.color
          events.push(event)
        }

        // 10.対沖, 12.天剋, 13.納音
        if (
          taichuShis.includes(targetNittyuShi + userShi) ||
                    taichuShis.includes(userShi + targetNittyuShi)
        ) {
          // 納音判定
          // event追加
          if (targetNittyuKan === userKan) {
            const event = Object.create(eventTemplate)
            event.name = this.sanmeiPhase.nacchin.name
            event.color = this.sanmeiPhase.nacchin.color
            events.push(event)
          } else {
            // 天剋判定
            if (
              tenkokuKans.includes(targetNittyuKan + userKan) ||
                            tenkokuKans.includes(userKan + targetNittyuKan)
            ) {
              const event = Object.create(eventTemplate)
              event.name = this.sanmeiPhase.tenkoku.name
              event.color = this.sanmeiPhase.tenkoku.color
              events.push(event)
            } else {
              // 対沖
              const event = Object.create(eventTemplate)
              event.name = this.sanmeiPhase.taichu.name
              event.color = this.sanmeiPhase.taichu.color
              events.push(event)
            }
          }
        }

        // 11.破
        if (
          haShis.includes(targetNittyuShi + userShi) ||
                    haShis.includes(userShi + targetNittyuShi)
        ) {
          const event = Object.create(eventTemplate)
          event.name = this.sanmeiPhase.ha.name
          event.color = this.sanmeiPhase.ha.color
          events.push(event)
        }

        // 干支番号を更新
        targetEtoDayIndex = (targetEtoDayIndex + 1) % 60
      }
      return events
    },
    createSafeDay() {
      const events = []

      // 日ごとの位相情報計算のベース日付（月の始めと終わり）
      const targetDateYYYYMM = this.start.date.slice(0, 8) // YYYY-MM-形式
      // 対象月の始めの日の干支位置計算
      let targetEtoDayIndex = this.etoDayIndex(this.start.date)

      // 日ごとの位相情報を計算
      // 対象期間(月)分ループ
      for (let i = this.start.day; i <= this.end.day; i++) {
        const targetDateDD = ('00' + i).slice(-2)
        const targetDateYYYYMMDD = `${targetDateYYYYMM}${targetDateDD}`

        // 必要情報取得
        const targetNittyu = this.eto60[targetEtoDayIndex]
        // const targetNittyuKan = targetNittyu.slice(0, 1)
        const targetNittyuShi = targetNittyu.slice(-1)

        //let userEtoIndex, userKan, userShi
        // userEtoIndex = this.user_meishiki.santyu.etoDayIndex
        // userKan = this.user_meishiki.santyu.day1
        let userShi = this.user_meishiki.santyu.day2

        const eventTemplate = {
          name: '',
          start: targetDateYYYYMMDD,
          end: targetDateYYYYMMDD,
          color: '',
          type: 'safeDay',
          nittyu: targetNittyu,
        }

        // 1.支合
        if (
          shigoShis.includes(targetNittyuShi + userShi) ||
                    shigoShis.includes(userShi + targetNittyuShi)
        ) {
          // 選日判定
          let isSafeDay = this.events.some(
            (event) =>
              event.start === targetDateYYYYMMDD &&
                            (event.name === '支合' &&
                                event.name !== '不成就日' ||
                                event.name !== '三隣亡')
          )
          // 六曜判定
          let rokuyou = !this.oldCalendar.datelist[targetDateYYYYMMDD]
            ? ''
            : this.oldCalendar.datelist[targetDateYYYYMMDD].rokuyou
          if (rokuyou === '赤口' || rokuyou === '仏滅') {
            isSafeDay = false
          }

          if (isSafeDay) {
            const event = Object.create(eventTemplate)
            event.name = '安牌日'
            event.color = 'green accent-1'
            event.nittyu = targetNittyu
            events.push(event)
          }
        }

        // 干支番号を更新
        targetEtoDayIndex = (targetEtoDayIndex + 1) % 60
      }
      return events
    },
    createSenjitsu(kan, shi, targetDateYYYYMMDD) {
      const events = []
      const eventTemplate = {
        name: '',
        start: targetDateYYYYMMDD,
        end: targetDateYYYYMMDD,
        color: '',
        type: 'senjitsu',
        nittyu: kan + shi,
      }
      // 正月：立春・雨水, 二月：啓蟄・春分
      // 三月：清明・穀雨, 四月：立夏・小満
      // 五月：芒種・夏至, 六月：小暑・大暑
      // 七月：立秋・処暑, 八月：白露・秋分
      // 九月：寒露・霜降, 十月：立冬・小雪
      // 十一月：大雪・冬至, 十二月：小寒・大寒
      const sekki = this.oldCalendar.datelist[targetDateYYYYMMDD].sekki
      const kyurekim = this.oldCalendar.datelist[targetDateYYYYMMDD]
        .kyurekim
      const kyurekid = this.oldCalendar.datelist[targetDateYYYYMMDD]
        .kyurekid

      // 三隣亡
      if (
        [
          '立春',
          '雨水',
          '立夏',
          '小満',
          '立秋',
          '処暑',
          '立冬',
          '小雪',
        ].includes(sekki) &&
                    shi === '亥' ||
                [
                  '啓蟄',
                  '春分',
                  '芒種',
                  '夏至',
                  '白露',
                  '秋分',
                  '大雪',
                  '冬至',
                ].includes(sekki) &&
                    shi === '寅' ||
                [
                  '清明',
                  '穀雨',
                  '小暑',
                  '大暑',
                  '寒露',
                  '霜降',
                  '小寒',
                  '大寒',
                ].includes(sekki) &&
                    shi === '午'
      ) {
        const event = Object.create(eventTemplate)
        event.name = this.senjitsu.sanrinbo.name
        event.color = this.senjitsu.sanrinbo.color
        events.push(event)
      }

      // 一粒万倍日
      if (
        ['立春', '雨水'].includes(sekki) &&
                    ['丑', '午'].includes(shi) ||
                ['啓蟄', '春分'].includes(sekki) &&
                    ['酉', '寅'].includes(shi) ||
                ['清明', '穀雨'].includes(sekki) &&
                    ['子', '卯'].includes(shi) ||
                ['立夏', '小満'].includes(sekki) &&
                    ['卯', '辰'].includes(shi) ||
                ['芒種', '夏至'].includes(sekki) &&
                    ['巳', '午'].includes(shi) ||
                ['小暑', '大暑'].includes(sekki) &&
                    ['酉', '午'].includes(shi) ||
                ['立秋', '処暑'].includes(sekki) &&
                    ['子', '未'].includes(shi) ||
                ['白露', '秋分'].includes(sekki) &&
                    ['卯', '申'].includes(shi) ||
                ['寒露', '霜降'].includes(sekki) &&
                    ['酉', '午'].includes(shi) ||
                ['立冬', '小雪'].includes(sekki) &&
                    ['酉', '戌'].includes(shi) ||
                ['大雪', '冬至'].includes(sekki) &&
                    ['亥', '子'].includes(shi) ||
                ['小寒', '大寒'].includes(sekki) && ['卯', '子'].includes(shi)
      ) {
        const event = Object.create(eventTemplate)
        event.name = this.senjitsu.ichiryu.name
        event.color = this.senjitsu.ichiryu.color
        events.push(event)
      }

      // 不成就日
      if (
        [1, 7].includes(kyurekim) &&
                    [3, 11, 19, 27].includes(kyurekid) ||
                [2, 8].includes(kyurekim) &&
                    [2, 10, 18, 26].includes(kyurekid) ||
                [3, 9].includes(kyurekim) &&
                    [1, 9, 17, 25].includes(kyurekid) ||
                [4, 10].includes(kyurekim) &&
                    [4, 12, 20, 28].includes(kyurekid) ||
                [5, 11].includes(kyurekim) &&
                    [5, 13, 21, 29].includes(kyurekid) ||
                [6, 12].includes(kyurekim) &&
                    [6, 14, 22, 30].includes(kyurekid)
      ) {
        const event = Object.create(eventTemplate)
        event.name = this.senjitsu.fujoujunichi.name
        event.color = this.senjitsu.fujoujunichi.color
        events.push(event)
      }

      // 下段：天赦日
      const kanshi = kan + shi
      if (
        ['立春', '雨水', '啓蟄', '春分', '清明', '穀雨'].includes(
          sekki
        ) &&
                    kanshi === '戊寅' ||
                ['立夏', '小満', '芒種', '夏至', '小暑', '大暑'].includes(
                  sekki
                ) &&
                    kanshi === '甲午' ||
                ['立秋', '処暑', '白露', '秋分', '寒露', '霜降'].includes(
                  sekki
                ) &&
                    kanshi === '戊申' ||
                ['立冬', '小雪', '大雪', '冬至', '小寒', '大寒'].includes(
                  sekki
                ) &&
                    kanshi === '甲子'
      ) {
        const event = Object.create(eventTemplate)
        event.name = this.gedan.tenshabi.name
        event.color = this.gedan.tenshabi.color
        events.push(event)
      }

      return events
    },
    changeEventType(eventKey) {
      this.eventType = eventKey
    },
    async downloadCSV() {
      // validation
      if (!moment(this.csvStartDate).isSameOrBefore(this.csvEndDate, 'month')) {
        this.errorDialog = true
        this.errorMessage = '「開始年月」は「終了年月」以前を選択してください。'
        return
      }
      if (moment(this.csvEndDate).diff(this.csvStartDate, 'month') > 36) {
        this.errorDialog = true
        this.errorMessage = '対象期間は3年以内で選択してください。'
        return
      }

      let downloadData = []
      const startDate = moment(this.csvStartDate).format('YYYY-MM-DD')
      const endDate = moment(this.csvEndDate).format('YYYY-MM-DD')

      let downloadEvents = await this.createSanmeiPhaseDownload(
        this.eventType,
        startDate,
        endDate
      )
      downloadEvents.forEach((event) => {
        let annotation
        switch (this.eventType) {
        case 'oneself':
          if (event.type === 'oneself') {
            annotation = this.buildAnnotationOneself(event)
          } else if (event.type === 'senjitsu') {
            annotation = this.buildAnnotationSenjitsu(event)
          } else {
            return
          }
          break
        case 'work':
          if (event.type === 'work') {
            annotation = this.buildAnnotationWork(event)
          } else if (event.type === 'senjitsu') {
            annotation = this.buildAnnotationSenjitsu(event)
          } else {
            return
          }
          break
        case 'society':
          if (event.type === 'society') {
            annotation = this.buildAnnotationSociety(event)
          } else if (event.type === 'senjitsu') {
            annotation = this.buildAnnotationSenjitsu(event)
          } else {
            return
          }
          break
        case 'safeDay':
          if (event.type === 'safeDay') {
            annotation = '安牌日'
          } else if (
            event.name === '一粒万倍日' ||
                            event.name === '天赦日'
          ) {
            annotation = this.buildAnnotationSenjitsu(event)
          } else {
            return
          }
          break
        }
        downloadData.push({
          subject:
                        this.eventTypeToLabel[this.eventType] +
                        ':' +
                        event.name,
          startDate: event.start,
          startTime: event.start,
          description: annotation + '\n' + 'https://www.sai-labo.co.jp/calendar',
        })
      })
      this.csvTitle =
                '自分カレンダー_' +
                this.eventTypeToLabel[this.eventType] +
                '_' +
                startDate.substr(0, 7) +
                '～' +
                endDate.substr(0, 7)
      return downloadData
    },
  },
}
</script>

<style scoped lan="scss">
a {
    text-decoration: none;
}
</style>
